import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Building2, Users, Award, FileText, ChevronRight, 
  LogOut, Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyCommitteesPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchCommittees();
  }, []);

  const fetchCommittees = async () => {
    try {
      const response = await axios.get(`${API}/committees`);
      setCommittees(response.data);
      
      // Fetch stats for each committee
      const statsPromises = response.data.map(async (committee) => {
        try {
          // Get user's role in this committee
          const member = committee.members?.find(m => m.user_id === user?.id);
          const memberRole = member?.role;
          const isChairOrCoChair = memberRole === 'chair' || memberRole === 'co_chair';
          
          // Start with scholarships and applications (all members can view)
          const promises = [
            axios.get(`${API}/committees/${committee.id}/scholarships`),
            axios.get(`${API}/committees/${committee.id}/applications`)
          ];
          
          // Only fetch access requests if chair/co-chair
          if (isChairOrCoChair) {
            promises.push(axios.get(`${API}/committees/${committee.id}/access-requests`));
          }
          
          const results = await Promise.all(promises);
          const [scholarshipsRes, applicationsRes, requestsRes] = results;
          
          return {
            id: committee.id,
            memberRole: memberRole,
            scholarships: scholarshipsRes.data.length,
            pendingRequests: isChairOrCoChair && requestsRes ? requestsRes.data.filter(r => r.status === 'pending').length : null,
            submittedApplications: applicationsRes.data.filter(a => a.status === 'submitted').length
          };
        } catch {
          return { id: committee.id, memberRole: null, scholarships: 0, pendingRequests: null, submittedApplications: 0 };
        }
      });
      
      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(s => { statsMap[s.id] = s; });
      setStats(statsMap);
    } catch (error) {
      console.error('Error fetching committees:', error);
      toast.error('Failed to load committees');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const getUserRole = (committee) => {
    const member = committee.members?.find(m => m.user_id === user?.id);
    if (!member) return null;
    const roleLabels = {
      chair: 'Chair',
      co_chair: 'Co-Chair',
      member: 'Member'
    };
    return roleLabels[member.role] || member.role;
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'Chair': return 'bg-greek-gold text-white';
      case 'Co-Chair': return 'bg-greek-blue text-white';
      default: return 'bg-slate-200 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-greek-blue rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-900">My Committees</h1>
                <p className="text-sm text-slate-500">Welcome back, {user?.first_name}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-sm border-slate-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome Message */}
        <div className="bg-gradient-to-r from-greek-blue to-greek-blue-dark rounded-lg p-6 mb-8 text-white">
          <h2 className="text-2xl font-bold mb-2">AHEPA Scholarship Committee Portal</h2>
          <p className="text-blue-100">
            You are a member of {committees.length} committee{committees.length !== 1 ? 's' : ''}. 
            Select a committee below to manage scholarships, review applications, and more.
          </p>
        </div>

        {/* Committee Cards */}
        {committees.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Committees Yet</h3>
            <p className="text-slate-600 mb-6">You haven't been added to any committees yet.</p>
            <p className="text-sm text-slate-500">
              Contact an administrator to be invited to a committee.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {committees.map((committee) => {
              const role = getUserRole(committee);
              const committeeStats = stats[committee.id] || {};
              
              return (
                <div
                  key={committee.id}
                  className="bg-white rounded-lg border border-slate-200 hover:border-greek-blue/50 hover:shadow-lg transition-all overflow-hidden"
                >
                  {/* Committee Header */}
                  <div className="p-6 border-b border-slate-100">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-greek-blue/10 rounded-lg flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-greek-blue" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">{committee.name}</h3>
                          <p className="text-sm text-slate-500">{committee.description || 'Scholarship Committee'}</p>
                        </div>
                      </div>
                      {role && (
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getRoleBadgeClass(role)}`}>
                          {role}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className={`grid ${committeeStats.pendingRequests !== null ? 'grid-cols-3' : 'grid-cols-2'} divide-x divide-slate-100`}>
                    <div className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1 text-slate-500 mb-1">
                        <Award className="w-4 h-4" />
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{committeeStats.scholarships || 0}</p>
                      <p className="text-xs text-slate-500">Scholarships</p>
                    </div>
                    {committeeStats.pendingRequests !== null && (
                      <div className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1 text-amber-500 mb-1">
                          <Clock className="w-4 h-4" />
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{committeeStats.pendingRequests}</p>
                        <p className="text-xs text-slate-500">Pending Requests</p>
                      </div>
                    )}
                    <div className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1 text-blue-500 mb-1">
                        <FileText className="w-4 h-4" />
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{committeeStats.submittedApplications || 0}</p>
                      <p className="text-xs text-slate-500">Applications</p>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="p-4 bg-slate-50">
                    <Button
                      onClick={() => navigate(`/committee-dashboard?committee=${committee.id}`)}
                      className="w-full bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
                      data-testid={`open-committee-${committee.id}`}
                    >
                      Open Committee Dashboard
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Quick Actions */}
        {committees.length > 0 && (
          <div className="mt-8 bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              {user?.role === 'admin' && (
                <Link to="/admin">
                  <Button variant="outline" className="rounded-sm">
                    Go to Admin Dashboard
                  </Button>
                </Link>
              )}
              {/* Only show "Review Pending Requests" if user is chair/co-chair in at least one committee */}
              {committees.some(c => {
                const memberRole = c.members?.find(m => m.user_id === user?.id)?.role;
                return memberRole === 'chair' || memberRole === 'co_chair';
              }) && (
                <Button 
                  variant="outline" 
                  className="rounded-sm"
                  onClick={() => {
                    const committeeWithRequests = committees.find(c => {
                      const memberRole = c.members?.find(m => m.user_id === user?.id)?.role;
                      return (memberRole === 'chair' || memberRole === 'co_chair') && (stats[c.id]?.pendingRequests || 0) > 0;
                    });
                    if (committeeWithRequests) {
                      navigate(`/committee-dashboard?committee=${committeeWithRequests.id}&tab=requests`);
                    } else {
                      toast.info('No pending access requests');
                    }
                  }}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Review Pending Requests
                </Button>
              )}
              <Button 
                variant="outline" 
                className="rounded-sm"
                onClick={() => {
                  const committeeWithApps = committees.find(c => (stats[c.id]?.submittedApplications || 0) > 0);
                  if (committeeWithApps) {
                    navigate(`/committee-dashboard?committee=${committeeWithApps.id}&tab=applicants`);
                  } else {
                    toast.info('No submitted applications to review');
                  }
                }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Review Applications
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyCommitteesPage;
